import type { Contact } from '../../../../types/contact.types';
import type { GQLPropertyEnrichmentItem } from '../../../../hooks/graphql/contacts/useContact';
import { formatCurrency } from '../../../../utils/formatters';

type ProcessedData = {
  propertyDetails?: Record<string, unknown>;
  rawApiResponse?: { propertyDetails?: Record<string, unknown> };
  estimatedValue?: number;
  yearBuilt?: number;
  propertyType?: string;
  squareFootage?: number;
  bedrooms?: number;
  bathrooms?: number;
  lotSize?: number;
};

export interface HomeownerSignal {
  tier: 'high' | 'med' | 'low';
  title: string;
  strength: string;
  detail: string;
}

export interface SaleRow {
  date?: string;
  price?: number;
  amount?: number;
  documentType?: string;
  seller?: string;
  buyer?: string;
}

export interface MortgageRow {
  lender?: string;
  amount?: number;
  loanAmount?: number;
  recordDate?: string;
  date?: string;
  loanType?: string;
  type?: string;
  interestRate?: number;
  rate?: number;
}

export interface SchoolRow {
  name?: string;
  type?: string;
  level?: string;
  rating?: number;
  score?: number;
  district?: string;
  grades?: string;
}

export interface HomeownerSummary {
  hasLive: boolean;
  address: string;
  estValue: string | null;
  valueChange: string | null;
  valueChangePct: number | null;
  propertyType: string | null;
  beds: number | null;
  baths: number | null;
  sqft: number | null;
  yearBuilt: number | null;
  lotSqft: number | null;
  lastSaleDate: string | null;
  lastSaleAmount: string | null;
  floodZone: string | null;
  floodZoneType: string | null;
  ownerOccupied: boolean | null;
  ownerName1: string | null;
  ownerName2: string | null;
  yearsOwned: number | null;
  yearsOwnedServer: number | null;
  estimatedEquity: string | null;
  equity: string | null;
  equityPercent: number | null;
  highEquity: boolean | null;
  negativeEquity: boolean | null;
  freeClear: boolean | null;
  neighborhood: string | null;
  neighborhoodType: string | null;
  pricePerSquareFoot: string | null;
  ownerType: string | null;
  taxTotal: string | null;
  taxImprovement: string | null;
  taxLand: string | null;
  taxYear: number | null;
  annualTaxes: string | null;
  assessedValue: string | null;
  assessedImprovementValue: string | null;
  assessedLandValue: string | null;
  improvementValuePercent: number | null;
  parcelId: string | null;
  propertyId: string | null;
  apn: string | null;
  fips: string | null;
  county: string | null;
  lastUpdateDate: string | null;
  landUse: string | null;
  propertyUse: string | null;
  propertyUseCode: string | null;
  documentType: string | null;
  documentTypeCode: string | null;
  recordingDate: string | null;
  vacant: boolean | null;
  corporateOwned: boolean | null;
  absenteeOwner: boolean | null;
  inStateAbsenteeOwner: boolean | null;
  outOfStateAbsenteeOwner: boolean | null;
  mailingAddress: string | null;
  mailingCounty: string | null;
  roomsCount: number | null;
  stories: number | null;
  unitsCount: number | null;
  basement: boolean | null;
  garage: boolean | null;
  pool: boolean | null;
  poolArea: number | null;
  deck: boolean | null;
  deckArea: number | null;
  patio: boolean | null;
  patioArea: number | null;
  airConditioningAvailable: boolean | null;
  hoa: boolean | null;
  roofConstruction: string | null;
  roofMaterial: string | null;
  mfh2to4: boolean | null;
  mfh5plus: boolean | null;
  lenderName: string | null;
  openMortgageBalance: string | null;
  openMortgagePercent: number | null;
  firstMortgagePercent: number | null;
  secondMortgagePercent: number | null;
  thirdMortgagePercent: number | null;
  lastMortgage1Amount: string | null;
  estimatedMortgagePayment: string | null;
  adjustableRate: boolean | null;
  assumable: boolean | null;
  privateLender: boolean | null;
  cashBuyer: boolean | null;
  investorBuyer: boolean | null;
  foreclosureStatus: string | null;
  foreclosureFilingDate: string | null;
  foreclosureAuctionDate: string | null;
  foreclosureAmount: string | null;
  preForeclosure: boolean | null;
  reo: boolean | null;
  auction: boolean | null;
  auctionDate: string | null;
  noticeType: string | null;
  taxLien: boolean | null;
  judgment: boolean | null;
  death: boolean | null;
  inherited: boolean | null;
  forSale: boolean | null;
  priceReduced: boolean | null;
  listingAmount: string | null;
  listingPriceToValuePercent: number | null;
  mlsActive: boolean | null;
  mlsPending: boolean | null;
  mlsSold: boolean | null;
  mlsCancelled: boolean | null;
  mlsFailed: boolean | null;
  mlsHasPhotos: boolean | null;
  mlsStatus: string | null;
  mlsListingPrice: string | null;
  mlsListingDate: string | null;
  mlsLastSaleDate: string | null;
  mlsLastStatusDate: string | null;
  mlsDaysOnMarket: number | null;
  lastSaleArmsLength: boolean | null;
  latestArmsLengthSaleAmount: string | null;
  latestArmsLengthSaleDate: string | null;
  latestNonArmsLengthSaleDate: string | null;
  priorOwner: string | null;
  priorOwnerAddress: string | null;
  priorOwnerIndividual: boolean | null;
  priorOwnerMonthsOwned: number | null;
  priorSaleAmount: string | null;
  priorSaleDate: string | null;
  priorArmsLengthSaleAmount: string | null;
  priorArmsLengthSaleDate: string | null;
  rentAmount: string | null;
  medianIncome: string | null;
  salesHistory: SaleRow[];
  mortgages: MortgageRow[];
  schools: SchoolRow[];
  population: number | null;
  populationDensity: number | null;
  populationGrowth: number | null;
  medianHouseholdIncome: string | null;
  averageHouseholdSize: number | null;
  totalHouseholds: number | null;
  eduHighSchool: number | null;
  eduCollege: number | null;
  eduBachelors: number | null;
  rentalStudio: string | null;
  rentalOneBd: string | null;
  rentalTwoBd: string | null;
  rentalThreeBd: string | null;
  lat: number | null;
  lng: number | null;
  signals: HomeownerSignal[];
  updatedAt: string | null;
  lastEnrichmentAt: string | null;
  confidenceScore: number | null;
}

function parseEnrichment(enrichment?: GQLPropertyEnrichmentItem | null): ProcessedData | null {
  if (!enrichment?.processedData) return null;
  try {
    return JSON.parse(enrichment.processedData) as ProcessedData;
  } catch {
    return null;
  }
}

function parseRawApi(
  enrichment?: GQLPropertyEnrichmentItem | null,
): { propertyDetails?: Record<string, unknown>; demographics?: Record<string, unknown>; searchResults?: Record<string, unknown> } | null {
  if (!enrichment?.rawApiResponse) return null;
  try {
    return JSON.parse(enrichment.rawApiResponse) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Property summary derived from the live PropertyEnrichment record.
 * Every field reads from processedData.propertyDetails (or the raw API
 * fallback) with safe null guards — missing values surface as `null` so
 * the UI can hide them rather than render placeholder data.
 */
export function buildHomeownerSummary(
  contact: Contact,
  enrichment?: GQLPropertyEnrichmentItem | null,
): HomeownerSummary {
  const d = parseEnrichment(enrichment);
  const raw = parseRawApi(enrichment);

  // Merge: full RealEstateAPI propertyDetails (raw) as base, processed-and-normalised
  // overrides where it has a non-null value. Gives the panel every field from either source.
  const rawDetails =
    (raw?.propertyDetails as Record<string, unknown> | undefined) ??
    (d?.rawApiResponse?.propertyDetails as Record<string, unknown> | undefined) ??
    {};
  const processedDetails =
    (d?.propertyDetails as Record<string, unknown> | undefined) ??
    ((d as Record<string, unknown> | undefined) ?? {});
  const details: Record<string, unknown> = { ...rawDetails };
  for (const k in processedDetails) {
    const v = processedDetails[k];
    if (v !== null && v !== undefined) details[k] = v;
  }

  const enrichedAddr = details?.address as
    | {
        address?: string;
        street?: string;
        city?: string;
        state?: string;
        zip?: string;
        county?: string;
        fips?: string;
      }
    | undefined;
  const addressFromEnrichment = enrichedAddr
    ? [enrichedAddr.street ?? enrichedAddr.address, enrichedAddr.city, enrichedAddr.state, enrichedAddr.zip]
        .filter(Boolean)
        .join(', ')
    : '';
  const addressFromContact = contact.address
    ? [contact.address.street, contact.address.city, contact.address.state, contact.address.zip]
        .filter(Boolean)
        .join(', ')
    : '';
  const address = addressFromEnrichment || addressFromContact;

  // RealEstateAPI is inconsistent — money/percent fields come back as either
  // numbers or strings ("0"), and codes like `propertyUseCode` are numbers we
  // want to display as text. These helpers normalise both directions.
  const num = (v: unknown): number | null => {
    if (typeof v === 'number' && !isNaN(v)) return v;
    if (typeof v === 'string' && v.length > 0) {
      const parsed = Number(v);
      return isNaN(parsed) ? null : parsed;
    }
    return null;
  };
  const str = (v: unknown): string | null => {
    if (typeof v === 'string' && v.length > 0) return v;
    if (typeof v === 'number' && !isNaN(v)) return String(v);
    return null;
  };
  const bool = (v: unknown): boolean | null => (typeof v === 'boolean' ? v : null);
  const dateStr = (v: unknown): string | null => {
    const s = typeof v === 'string' && v.length > 0 ? v : null;
    if (!s) return null;
    const dt = new Date(s);
    return isNaN(dt.getTime()) ? s : dt.toLocaleDateString();
  };
  const money = (v: number | null): string | null => (v != null ? formatCurrency(v) : null);

  const estValue = num(details?.estimatedValue) ?? num(d?.estimatedValue);
  const yearBuilt = num(details?.yearBuilt) ?? num(d?.yearBuilt);
  const beds = num(details?.bedrooms) ?? num(d?.bedrooms);
  const baths = num(details?.bathrooms) ?? num(d?.bathrooms);
  const sqft = num(details?.squareFeet) ?? num(d?.squareFootage);
  const lotSqft = num(details?.lotSquareFeet) ?? num(d?.lotSize);
  const lastSaleDate = str(details?.lastSaleDate);
  const lastSaleAmount = num(details?.lastSaleAmount) ?? num(details?.lastSalePrice);
  // floodZone arrives as either a string ("AE", "X") or a boolean (in/out of zone).
  const floodZoneRaw = details?.floodZone;
  const floodZone =
    typeof floodZoneRaw === 'string' && floodZoneRaw.length > 0
      ? floodZoneRaw
      : typeof floodZoneRaw === 'boolean'
      ? floodZoneRaw
        ? 'In flood zone'
        : 'Not in flood zone'
      : str(details?.floodZoneDescription);
  const floodZoneType = str(details?.floodZoneType);
  const ownerOccupied = bool(details?.ownerOccupied);
  const estimatedEquity = num(details?.estimatedEquity);
  const equity = num(details?.equity);
  const equityPercent = num(details?.equityPercent);
  const highEquity = bool(details?.highEquity);
  const negativeEquity = bool(details?.negativeEquity);
  const freeClear = bool(details?.freeClear);
  const neighborhoodObj = details?.neighborhood as { name?: string; type?: string } | undefined;
  const neighborhood = str(neighborhoodObj?.name);
  const neighborhoodType = str(neighborhoodObj?.type);
  const propertyType = str(details?.propertyType) ?? str(d?.propertyType);
  const pricePerSquareFoot = num(details?.pricePerSquareFoot);
  const owner1First = str(details?.owner1FirstName);
  const owner1Last = str(details?.owner1LastName);
  const ownerName1 =
    [owner1First, owner1Last].filter(Boolean).join(' ') || str(details?.ownerName);
  const owner2First = str(details?.owner2FirstName);
  const owner2Last = str(details?.owner2LastName);
  const ownerName2 = [owner2First, owner2Last].filter(Boolean).join(' ') || null;
  const ownerTypeRaw = str(details?.ownerType);
  const ownerType = ownerTypeRaw
    ? ownerTypeRaw.charAt(0).toUpperCase() + ownerTypeRaw.slice(1).toLowerCase()
    : null;

  const tax = details?.taxAssessment as
    | {
        improvementValue?: number;
        landValue?: number;
        totalAssessedValue?: number;
        taxYear?: number;
        annualTaxes?: number;
      }
    | undefined;
  const taxTotal = num(tax?.totalAssessedValue);
  const taxImprovement = num(tax?.improvementValue);
  const taxLand = num(tax?.landValue);
  const taxYear = num(tax?.taxYear);
  const annualTaxes = num(tax?.annualTaxes);
  const assessedValue = num(details?.assessedValue);
  const assessedImprovementValue = num(details?.assessedImprovementValue);
  const assessedLandValue = num(details?.assessedLandValue);
  const improvementValuePercent = num(details?.improvementValuePercent);

  const parcelId = str(details?.parcelId);
  const apn = str(details?.apn) ?? parcelId;
  const propertyId = str(details?.id) ?? str(details?.propertyId);
  const fips = str(enrichedAddr?.fips);
  const county = str(enrichedAddr?.county);
  const lastUpdateDate = dateStr(details?.lastUpdateDate);
  const landUse = str(details?.landUse);
  const propertyUse = str(details?.propertyUse);
  const propertyUseCode = str(details?.propertyUseCode);
  const documentType = str(details?.documentType);
  const documentTypeCode = str(details?.documentTypeCode);
  const recordingDate = dateStr(details?.recordingDate);

  const vacant = bool(details?.vacant);
  const corporateOwned = bool(details?.corporateOwned);
  const absenteeOwner = bool(details?.absenteeOwner);
  const inStateAbsenteeOwner = bool(details?.inStateAbsenteeOwner);
  const outOfStateAbsenteeOwner = bool(details?.outOfStateAbsenteeOwner);

  const mailing = details?.mailAddress as
    | {
        address?: string;
        street?: string;
        city?: string;
        state?: string;
        zip?: string;
        county?: string;
      }
    | undefined;
  const mailingAddress = mailing
    ? [mailing.street ?? mailing.address, mailing.city, mailing.state, mailing.zip]
        .filter(Boolean)
        .join(', ') || null
    : null;
  const mailingCounty = str(mailing?.county);

  const roomsCount = num(details?.roomsCount);
  const stories = num(details?.stories);
  const unitsCount = num(details?.unitsCount);
  const basement = bool(details?.basement);
  const garage = bool(details?.garage);
  const pool = bool(details?.pool);
  const poolArea = num(details?.poolArea);
  const deck = bool(details?.deck);
  const deckArea = num(details?.deckArea);
  const patio = bool(details?.patio);
  const patioArea = num(details?.patioArea);
  const airConditioningAvailable = bool(details?.airConditioningAvailable);
  const hoa = bool(details?.hoa);
  const roofConstruction = str(details?.roofConstruction);
  const roofMaterial = str(details?.roofMaterial);
  const mfh2to4 = bool(details?.MFH2to4);
  const mfh5plus = bool(details?.MFH5plus);

  const lenderName = str(details?.lenderName);
  const openMortgageBalance = num(details?.openMortgageBalance);
  const openMortgagePercent = num(details?.openMortgagePercent);
  const firstMortgagePercent = num(details?.firstMortgagePercent);
  const secondMortgagePercent = num(details?.secondMortgagePercent);
  const thirdMortgagePercent = num(details?.thirdMortgagePercent);
  const lastMortgage1Amount = num(details?.lastMortgage1Amount);
  const estimatedMortgagePayment = num(details?.estimatedMortgagePayment);
  const adjustableRate = bool(details?.adjustableRate);
  const assumable = bool(details?.assumable);
  const privateLender = bool(details?.privateLender);
  const cashBuyer = bool(details?.cashBuyer);
  const investorBuyer = bool(details?.investorBuyer);

  // `foreclosure` is sometimes a rich object, sometimes just a boolean — handle both.
  const foreclosureRaw = details?.foreclosure;
  const foreclosureObj =
    foreclosureRaw && typeof foreclosureRaw === 'object'
      ? (foreclosureRaw as {
          status?: string;
          filingDate?: string;
          auctionDate?: string;
          amount?: number;
          debtAmount?: number;
        })
      : undefined;
  const foreclosureStatus =
    str(foreclosureObj?.status) ??
    (typeof foreclosureRaw === 'boolean'
      ? foreclosureRaw
        ? 'In foreclosure'
        : 'Not in foreclosure'
      : null);
  const foreclosureFilingDate = str(foreclosureObj?.filingDate);
  const foreclosureAuctionDate = str(foreclosureObj?.auctionDate);
  const foreclosureAmount = num(foreclosureObj?.amount) ?? num(foreclosureObj?.debtAmount);
  const preForeclosure = bool(details?.preForeclosure);
  const reo = bool(details?.reo);
  const auction = bool(details?.auction);
  const auctionDate = dateStr(details?.auctionDate);
  const noticeType = str(details?.noticeType);
  const taxLien = bool(details?.taxLien);
  const judgment = bool(details?.judgment);
  const death = bool(details?.death);
  const inherited = bool(details?.inherited);
  const forSale = bool(details?.forSale);
  const priceReduced = bool(details?.priceReduced);
  const listingAmount = num(details?.listingAmount);
  const listingPriceToValuePercent = num(details?.listingPriceToValuePercent);

  const mlsActive = bool(details?.mlsActive);
  const mlsPending = bool(details?.mlsPending);
  const mlsSold = bool(details?.mlsSold);
  const mlsCancelled = bool(details?.mlsCancelled);
  const mlsFailed = bool(details?.mlsFailed);
  const mlsHasPhotos = bool(details?.mlsHasPhotos);
  const mlsStatus = str(details?.mlsStatus);
  const mlsListingPrice = num(details?.mlsListingPrice);
  const mlsListingDate = dateStr(details?.mlsListingDate);
  const mlsLastSaleDate = dateStr(details?.mlsLastSaleDate);
  const mlsLastStatusDate = dateStr(details?.mlsLastStatusDate);
  const mlsDaysOnMarket = num(details?.mlsDaysOnMarket);

  const lastSaleArmsLength = bool(details?.lastSaleArmsLength);
  const latestArmsLengthSaleAmount = num(details?.latestArmsLengthSaleAmount);
  const latestArmsLengthSaleDate = dateStr(details?.latestArmsLengthSaleDate);
  const latestNonArmsLengthSaleDate = dateStr(details?.latestNonArmsLengthSaleDate);

  const priorOwner = str(details?.priorOwner);
  const priorOwnerAddress = str(details?.priorOwnerAddress);
  const priorOwnerIndividual = bool(details?.priorOwnerIndividual);
  const priorOwnerMonthsOwned = num(details?.priorOwnerMonthsOwned);
  const priorSaleAmount = num(details?.priorSaleAmount);
  const priorSaleDate = dateStr(details?.priorSaleDate);
  const priorArmsLengthSaleAmount = num(details?.priorArmsLengthSaleAmount);
  const priorArmsLengthSaleDate = dateStr(details?.priorArmsLengthSaleDate);

  const rentAmount = num(details?.rentAmount);
  const medianIncome = num(details?.medianIncome);

  const salesHistory = Array.isArray(details?.salesHistory)
    ? (details?.salesHistory as SaleRow[])
    : [];
  const mortgages = Array.isArray(details?.mortgages)
    ? (details?.mortgages as MortgageRow[])
    : [];
  const schools = Array.isArray(details?.schools)
    ? (details?.schools as SchoolRow[])
    : [];

  // Demographics: processed data normalises it under `details.demographics`, but the
  // raw RealEstateAPI payload exposes it at `rawApiResponse.demographics` (sibling
  // of propertyDetails). Fall back to the raw shape so we don't drop those fields.
  const demo = (details?.demographics ??
    (raw?.demographics as Record<string, unknown> | undefined)) as
    | {
        population?: number;
        populationDensity?: number;
        populationGrowth?: number;
        medianHouseholdIncome?: number;
        averageHouseholdSize?: number;
        totalHouseholds?: number;
        educationLevel?: {
          highSchoolGraduates?: number;
          collegeGraduates?: number;
          bachelorsDegree?: number;
        };
      }
    | undefined;
  const population = num(demo?.population);
  const populationDensity = num(demo?.populationDensity);
  const populationGrowth = num(demo?.populationGrowth);
  const medianHouseholdIncome = num(demo?.medianHouseholdIncome);
  const averageHouseholdSize = num(demo?.averageHouseholdSize);
  const totalHouseholds = num(demo?.totalHouseholds);
  const eduHighSchool = num(demo?.educationLevel?.highSchoolGraduates);
  const eduCollege = num(demo?.educationLevel?.collegeGraduates);
  const eduBachelors = num(demo?.educationLevel?.bachelorsDegree);

  const rental = details?.rentalEstimates as
    | { studio?: number; oneBedroom?: number; twoBedroom?: number; threeBedroom?: number }
    | undefined;
  const rentalStudio = num(rental?.studio);
  const rentalOneBd = num(rental?.oneBedroom);
  const rentalTwoBd = num(rental?.twoBedroom);
  const rentalThreeBd = num(rental?.threeBedroom);

  const coords = (details?.coordinates ?? (d as Record<string, unknown> | undefined)?.coordinates) as
    | { latitude?: number; longitude?: number }
    | undefined;
  const lat = num(coords?.latitude) ?? num(details?.latitude);
  const lng = num(coords?.longitude) ?? num(details?.longitude);

  const flatPropertyAddress = str((d as Record<string, unknown> | undefined)?.propertyAddress);
  const finalAddress = address || flatPropertyAddress || '';

  const yearsOwned =
    lastSaleDate && !isNaN(new Date(lastSaleDate).getFullYear())
      ? new Date().getFullYear() - new Date(lastSaleDate).getFullYear()
      : null;
  const yearsOwnedServer = num(details?.yearsOwned);

  const valueChange =
    estValue && lastSaleAmount && lastSaleAmount > 0
      ? Math.round(((estValue - lastSaleAmount) / lastSaleAmount) * 100)
      : null;

  const signals: HomeownerSignal[] = [];
  if (yearsOwned !== null && yearsOwned >= 8) {
    signals.push({
      tier: 'high',
      title: 'Long tenure',
      strength: 'STRONG',
      detail: `Owned for ${yearsOwned} years — well past the typical 7-year holding period.`,
    });
  }
  if (valueChange !== null && valueChange >= 50) {
    signals.push({
      tier: 'high',
      title: 'High equity event',
      strength: 'STRONG',
      detail: `Estimated value is up ${valueChange}% since purchase — strong incentive to move up or cash out.`,
    });
  }
  if (
    typeof contact.score === 'number' &&
    contact.score >= 75 &&
    contact.leadIntent !== 'Seller'
  ) {
    signals.push({
      tier: 'med',
      title: 'Actively shopping',
      strength: 'MEDIUM',
      detail: 'High engagement score — a buy-then-sell sequence may be in play.',
    });
  }

  return {
    hasLive: Boolean(d),
    address: finalAddress || '— address not on file —',
    estValue: money(estValue),
    valueChange: valueChange !== null ? `${valueChange >= 0 ? '+' : ''}${valueChange}%` : null,
    valueChangePct: valueChange,
    propertyType,
    beds,
    baths,
    sqft,
    yearBuilt,
    lotSqft,
    lastSaleDate,
    lastSaleAmount: money(lastSaleAmount),
    floodZone,
    floodZoneType,
    ownerOccupied,
    ownerName1,
    ownerName2,
    yearsOwned,
    yearsOwnedServer,
    estimatedEquity: money(estimatedEquity),
    equity: money(equity),
    equityPercent,
    highEquity,
    negativeEquity,
    freeClear,
    neighborhood,
    neighborhoodType,
    pricePerSquareFoot: money(pricePerSquareFoot),
    ownerType,
    taxTotal: money(taxTotal),
    taxImprovement: money(taxImprovement),
    taxLand: money(taxLand),
    taxYear,
    annualTaxes: money(annualTaxes),
    assessedValue: money(assessedValue),
    assessedImprovementValue: money(assessedImprovementValue),
    assessedLandValue: money(assessedLandValue),
    improvementValuePercent,
    parcelId,
    propertyId,
    apn,
    fips,
    county,
    lastUpdateDate,
    landUse,
    propertyUse,
    propertyUseCode,
    documentType,
    documentTypeCode,
    recordingDate,
    vacant,
    corporateOwned,
    absenteeOwner,
    inStateAbsenteeOwner,
    outOfStateAbsenteeOwner,
    mailingAddress,
    mailingCounty,
    roomsCount,
    stories,
    unitsCount,
    basement,
    garage,
    pool,
    poolArea,
    deck,
    deckArea,
    patio,
    patioArea,
    airConditioningAvailable,
    hoa,
    roofConstruction,
    roofMaterial,
    mfh2to4,
    mfh5plus,
    lenderName,
    openMortgageBalance: money(openMortgageBalance),
    openMortgagePercent,
    firstMortgagePercent,
    secondMortgagePercent,
    thirdMortgagePercent,
    lastMortgage1Amount: money(lastMortgage1Amount),
    estimatedMortgagePayment: money(estimatedMortgagePayment),
    adjustableRate,
    assumable,
    privateLender,
    cashBuyer,
    investorBuyer,
    foreclosureStatus,
    foreclosureFilingDate,
    foreclosureAuctionDate,
    foreclosureAmount: money(foreclosureAmount),
    preForeclosure,
    reo,
    auction,
    auctionDate,
    noticeType,
    taxLien,
    judgment,
    death,
    inherited,
    forSale,
    priceReduced,
    listingAmount: money(listingAmount),
    listingPriceToValuePercent,
    mlsActive,
    mlsPending,
    mlsSold,
    mlsCancelled,
    mlsFailed,
    mlsHasPhotos,
    mlsStatus,
    mlsListingPrice: money(mlsListingPrice),
    mlsListingDate,
    mlsLastSaleDate,
    mlsLastStatusDate,
    mlsDaysOnMarket,
    lastSaleArmsLength,
    latestArmsLengthSaleAmount: money(latestArmsLengthSaleAmount),
    latestArmsLengthSaleDate,
    latestNonArmsLengthSaleDate,
    priorOwner,
    priorOwnerAddress,
    priorOwnerIndividual,
    priorOwnerMonthsOwned,
    priorSaleAmount: money(priorSaleAmount),
    priorSaleDate,
    priorArmsLengthSaleAmount: money(priorArmsLengthSaleAmount),
    priorArmsLengthSaleDate,
    rentAmount: money(rentAmount),
    medianIncome: money(medianIncome),
    salesHistory,
    mortgages,
    schools,
    population,
    populationDensity,
    populationGrowth,
    medianHouseholdIncome: money(medianHouseholdIncome),
    averageHouseholdSize,
    totalHouseholds,
    eduHighSchool,
    eduCollege,
    eduBachelors,
    rentalStudio: money(rentalStudio),
    rentalOneBd: money(rentalOneBd),
    rentalTwoBd: money(rentalTwoBd),
    rentalThreeBd: money(rentalThreeBd),
    lat,
    lng,
    signals,
    updatedAt: enrichment?.updatedAt ?? null,
    lastEnrichmentAt: enrichment?.lastEnrichmentAt ?? null,
    confidenceScore: enrichment?.confidenceScore ?? null,
  };
}
